/**
 * Estatus:
 * 
 * 1. Activo
 * 2. Por autorizar
 * 3. Aprobado
 * 4. Rechazadas
 * 
 * **************
 * 
 * Restricciones:
 * 
 * Enviar a autorización:
 * [ Activo o Rechazadas ] 
 * (IdEstatus === 1 || 4) && (Es necesario ser usuario propio && tener permisos de usuario || Ser supervisor && tener permisos de supevisor || Ser tercer nivel && tener permisos de tercer nivel || El usuario se reporta a si mismo)
 *
 * Aprobar o rechazar:
 * [ Por autorizar ]
 * (IdEstatus === 2) && (Ser supervisor) && (No ser los ítems propios)
 * 
 * 
 * Regresar estatus a activo:
 * [ Aprobado ]
 * (IdEstatus === 3) && (Ser supervisor || Admin || El usuario se reporta a si mismo)
 */

import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { MUI, Typography } from '@styles';

const AuthInfo = (props) => {
  const {
    // Propiedades
    isAdmin,
    isSupervisor,
    isThirdLevel,
    reportHimself,
    permission,
    minItems,
    maxItems,
    totalItems,
    totalSum,
    ownItems,
    status,
    // Funciones
    onSendToAuthorize,
    onReturnToActiveStatus,
    onApprove,
    onReject
  } = props;

  const isResponsibilities = props.isResponsibilities;
  const isSetting = props.isSetting;
  const evaluation = props.evaluation;
  const category = props.category;
  const selectedEmployee = props.selectedEmployee;

  return (
    <>
      <Styled.BoxSection>
        <Styled.BoxInfo>
          {/* Mínimo */}
          <Typography.Text>
            {`${i18n.t('GenericMessages.min')}: `}
            <Typography.Strong>{minItems}</Typography.Strong>
          </Typography.Text>

          {/* Maximo */}
          <Typography.Text>
            {`${i18n.t('GenericMessages.max')}: `}
            <Typography.Strong>{maxItems}</Typography.Strong>
          </Typography.Text>

          {/* Comentario */}
          {((status.id === 3 || status.id === 4) && status.comment.length > 0) && (
            <Typography.Text>
              {`${i18n.t('GenericMessages.comment')}: `}
              <br />
              {status.comment}
            </Typography.Text>
          )}
        </Styled.BoxInfo>

        <Styled.BoxStatus>
          {/* Total */}
          <Typography.Text color={totalItems > maxItems ? 'red' : ''}>
            {`${i18n.t('GenericMessages.total')}: `}

            <Typography.Strong>{totalItems}</Typography.Strong>
          </Typography.Text>

          {/* Suma */}
          <Typography.Text color={totalSum > 100 ? 'red' : ''}>
            {`${i18n.t('GenericMessages.sum')}: `}
            <Typography.Strong>{totalSum}</Typography.Strong>
          </Typography.Text>

          {/* Estatus */}
          {(!evaluation || (evaluation && category.RequiereEstatus)) && (
            <Typography.Text>
              {`${i18n.t('GenericMessages.status')}: `}
              <Typography.Strong>{status.status}</Typography.Strong>
            </Typography.Text>
          )}
        </Styled.BoxStatus>
      </Styled.BoxSection>

      {/* Botones de usuario */}
      <Styled.BoxActions>
        {(status.id === 1 || status.id === 4) && ((ownItems && permission.user) || (isSupervisor && permission.supervisor) || (isThirdLevel && permission.thirdLevel) || (reportHimself && ownItems) || isAdmin) && (
          <MUI.ButtonCTA
            type="button"
            color="primary"
            disabled={totalSum !== 100}
            onClick={() => onSendToAuthorize()}
          >
            {!(reportHimself && ownItems) ? i18n.t('GenericMessages.sendToAuth') : i18n.t('DialogConfirm.approve')}
          </MUI.ButtonCTA>
        )}
        {(status.id === 3 && reportHimself && ownItems && !isAdmin && ((evaluation && evaluation.IdConfigEtapa === 2) || !evaluation)) && (
          <MUI.ButtonCTA
            type="button"
            color="primary"
            disabled={isAdmin && evaluation && !selectedEmployee.obj}
            onClick={() => onReturnToActiveStatus()}
          >
            {i18n.t('Responsibilities.revertStatus')}
          </MUI.ButtonCTA>
        )}
      </Styled.BoxActions>

      {/* Botones de supervisor */}
      {(!isAdmin && isSupervisor && !ownItems) && (
        <Styled.BoxActions>
          {(status.id === 2) && (
            <>
              <MUI.ButtonCTA
                type="button"
                color="secondary"
                onClick={() => onReject()}
              >
                {i18n.t('DialogConfirm.reject')}
              </MUI.ButtonCTA>
              <MUI.ButtonCTA
                type="button"
                color="primary"
                disabled={totalSum !== 100}
                onClick={() => onApprove()}
              >
                {i18n.t('DialogConfirm.approve')}
              </MUI.ButtonCTA>
            </>
          )}
          {(status.id === 3 && ((evaluation && evaluation.IdConfigEtapa === 2) || !evaluation)) && (
            <MUI.ButtonCTA
              type="button"
              color="primary"
              disabled={isAdmin && evaluation && !selectedEmployee.obj}
              onClick={() => onReturnToActiveStatus()}
            >
              {i18n.t('Responsibilities.revertStatus')}
            </MUI.ButtonCTA>
          )}
        </Styled.BoxActions>
      )}

      {/* Botones de administrador */}
      {isAdmin && (
        <Styled.BoxActions>
          {(isSetting && isResponsibilities && evaluation.IdConfigEtapa < 3) && (
            <MUI.ButtonCTA
              type="button"
              color="primary"
            >
              {i18n.t('GenericMessages.import')}
            </MUI.ButtonCTA>
          )}
          {(status.iprops.d === 2 && !ownItems && ((evaluation && evaluation.IdConfigEtapa < 3) || !evaluation)) && (
            <>
              <MUI.ButtonCTA
                type="button"
                color="secondary"
                disabled={evaluation && !selectedEmployee.obj}
                onClick={() => onReject()}
              >
                {i18n.t('DialogConfirm.reject')}
              </MUI.ButtonCTA>
              <MUI.ButtonCTA
                type="button"
                color="primary"
                disabled={totalSum !== 100 || (evaluation && !selectedEmployee.obj)}
                onClick={() => onApprove()}
              >
                {i18n.t('DialogConfirm.approve')}
              </MUI.ButtonCTA>
            </>
          )}
          {(status.id === 3 && ((evaluation && evaluation.IdConfigEtapa < 3) || !evaluation)) && (
            <MUI.ButtonCTA
              type="button"
              color="primary"
              disabled={evaluation && !selectedEmployee.obj}
              onClick={() => onReturnToActiveStatus()}
            >
              {i18n.t('Responsibilities.revertStatus')}
            </MUI.ButtonCTA>
          )}
        </Styled.BoxActions>
      )}
    </>
  );
};

AuthInfo.propTypes = {
  // Boolean que indica si es administrador.
  isAdmin: PropTypes.bool.isRequired,
  // Boolean que indica si es supervisor.
  isSupervisor: PropTypes.bool.isRequired,
  // Boolean que indica si es tercer nivel.
  isThirdLevel: PropTypes.bool.isRequired,
  // Boolean que indica si el usuario se reporta a sí mismo.
  reportHimself: PropTypes.bool.isRequired,
  // Permisos que tiene el usuario agregar responsabilidades.
  permission: PropTypes.object.isRequired,
  // Mínimo de elementos permitdos.
  minItems: PropTypes.number,
  // Maximo de elementos permitdos.
  maxItems: PropTypes.number,
  // Total de elementos.
  totalItems: PropTypes.number,
  // Suma total de los elementos.
  totalSum: PropTypes.number,
  // Boolean que indica si son elementos propios del usuario.
  ownItems: PropTypes.bool,
  // Objeto con propiedades del estatus de las responsabilidades (id, estatus y comentario).
  status: PropTypes.object,
  // Función que se ejecuta cuando se envián a autorización las responsabilidades.
  onSendToAuthorize: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se retorna el estatus de las responsabilidades.
  onReturnToActiveStatus: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se aprueban las responsabilidades.
  onApprove: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se rechazan las responsabilidades.
  onReject: PropTypes.func.isRequired,
  // Props pendientes.
  isResponsibilities: PropTypes.any,
  isSetting: PropTypes.any,
  evaluation: PropTypes.any,
  category: PropTypes.any,
  selectedEmployee: PropTypes.any
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxSection = styled.section`
  display: flex;
`;

Styled.BoxInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  width: 50%;
  padding: 8px;
`;

Styled.BoxStatus = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: flex-end;
  width: 50%;
  float: right;
  padding: 8px;
`;

Styled.BoxActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default AuthInfo;